/**
 * confidenceTier.ts — pure HSL-offset helper for PARA-02 confidence-tier material
 * treatment per Aesthetic Brief Rule 9.
 *
 * Source: 04-RESEARCH.md Pattern 3 + Pitfall #6 + AESTHETIC-BRIEF.md Rule 9.
 *
 * Three tiers produce visually distinguishable materials:
 *   - documented:  full palette saturation (no-op)
 *   - inferred:    7% desaturation + 2.3% darken  (subtle but present)
 *   - stand-in:   15% desaturation + 5% darken    (most pronounced)
 *
 * Why also darken (not just desaturate):
 *   Pitfall #6 — HSL's S axis is 0 for whites/grays. A near-white clapboard
 *   (#F8F8F8) would look identical across all three tiers with desaturation
 *   alone. Proportional lightness drop (desat/3) ensures the tier distinction
 *   holds for both colored AND achromatic surfaces.
 *
 * Why once at mount (not per-frame):
 *   Confidence never changes at runtime — an object's tier is fixed at author
 *   time (Blender custom prop → manifest entry). Applying at mount via R3F ref
 *   callback is O(1) per object-lifetime; per-frame would cost ~500 ops × 60fps.
 *
 * Why in-place mutation:
 *   meshStandardMaterial ref callback hands us the live material; mutating it
 *   is cheaper than constructing a replacement and lets R3F manage material
 *   disposal. The function signature (material, tier) => void communicates
 *   this contract.
 */
import * as THREE from 'three';

export type Confidence = 'documented' | 'inferred' | 'stand-in';

const TIER_MAP: Record<Confidence, { desat: number; darken: number }> = {
  documented: { desat: 0, darken: 0 },
  inferred: { desat: 0.07, darken: 0.07 / 3 }, // ~2.3% darker
  'stand-in': { desat: 0.15, darken: 0.15 / 3 }, // 5% darker
};

/**
 * Apply confidence-tier visual treatment to a three.js MeshStandardMaterial in place.
 *
 * Mutates `material.color` via `offsetHSL(0, -desat, -darken)`. Documented tier is
 * a no-op. Call exactly once at material mount (R3F ref callback) — repeated calls
 * with non-documented tiers will stack further shifts.
 *
 * @param material - the material to mutate
 * @param tier - the confidence tier
 */
export function applyConfidenceTreatment(
  material: THREE.MeshStandardMaterial,
  tier: Confidence,
): void {
  const { desat, darken } = TIER_MAP[tier];
  if (desat === 0 && darken === 0) return;
  material.color.offsetHSL(0, -desat, -darken);
}
